import dayjs from 'dayjs';

// PDF
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
(pdfMake as any).addVirtualFileSystem(pdfFonts);
// @ts-ignore
import QRious from 'qrious';
import type { Guia } from '../../interfaces/guia';
import { formatCuit } from '../../utils/formatCuit';
import { ROUTES } from '../../constants/routes-constants';
import guiaHeader from '../../assets/images/guia_header.png';
import { API_URL } from '../../constants/api';

// Utilidad: convierte una URL de imagen a dataURL (base64) para pdfMake
const imageUrlToDataUrl = async (url: string): Promise<string> => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
};

// Shared PDF styling/constants
const mm = 2.834645669; // 1mm in pt
const COLORS = {
  teal: '#00AEBC',
  pink: '#E91E63',
  grayText: '#333',
  rowGray: '#e9e9e9'
};

type TitleSizes = { title: number; law: number; sigem: number };

const buildHeaderImage = (headerBase64: string, pageWidthMm: number) => ({
  image: headerBase64,
  width: pageWidthMm * mm,
  margin: [0, 0, 0, 0]
});

const buildTitleStack = (
  sizes: TitleSizes,
  emisorTipoLabel = 'Productor / No productor',
  titleText = 'Guía de tránsito de minerales'
) => [
  {
    text: titleText,
    alignment: 'center',
    color: COLORS.pink,
    fontSize: sizes.title,
    bold: true
  },
  {
    text: 'Ley 13.312',
    alignment: 'center',
    color: COLORS.teal,
    fontSize: sizes.law,
    margin: [0, 2, 0, 2],
    bold: true
  },
  {
    text: `SGM - ${emisorTipoLabel}`,
    alignment: 'center',
    color: '#555',
    fontSize: sizes.sigem,
    bold: true
  }
];

const buildCenteredMeta = (label: string, value: string) => ({
  columns: [
    { width: '*', text: '' },
    {
      width: 'auto',
      table: {
        widths: ['auto', 'auto'],
        body: [
          [
            {
              text: label,
              bold: true,
              color: COLORS.teal,
              alignment: 'right',
              fontSize: 12,
              margin: [0, 0, 6, 0]
            },
            { text: value, color: '#555', alignment: 'left', fontSize: 12 }
          ]
        ]
      },
      layout: 'noBorders'
    },
    { width: '*', text: '' }
  ],
  margin: [0, 0, 0, 8]
});

const buildQrBlock = (qrBase64?: string) =>
  qrBase64
    ? {
        columns: [
          { width: '*', text: '' },
          {
            width: 'auto',
            table: {
              widths: [160],
              body: [[{ image: qrBase64, width: 160, alignment: 'center' }]]
            },
            layout: {
              hLineWidth: () => 2,
              vLineWidth: () => 2,
              hLineColor: '#000',
              vLineColor: '#000',
              paddingLeft: () => 6,
              paddingRight: () => 6,
              paddingTop: () => 6,
              paddingBottom: () => 6
            }
          },
          { width: '*', text: '' }
        ],
        margin: [0, 6, 0, 10]
      }
    : null;

const buildPreviewPlaceholder = () => ({
  columns: [
    { width: '*', text: '' },
    {
      width: 'auto',
      table: {
        widths: [160],
        body: [
          [
            {
              stack: [
                {
                  text: 'Esta no es una guía válida',
                  alignment: 'center',
                  fontSize: 10,
                  bold: true,
                  color: '#666',
                  margin: [0, 0, 0, 4]
                }
              ],
              alignment: 'center'
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 2,
        vLineWidth: () => 2,
        hLineColor: '#000',
        vLineColor: '#000',
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 6,
        paddingBottom: () => 6
      }
    },
    { width: '*', text: '' }
  ],
  margin: [0, 6, 0, 10]
});

// infoRow factory with zebra and fixed label width
export const createInfoRowFactory = (options: { labelWidth: number; startGray?: boolean }) => {
  let rowIndex = options.startGray ? 0 : 1; // start gray if true
  return (label: string, value: string) => {
    const isGray = rowIndex % 2 === 0;
    rowIndex += 1;
    return {
      table: {
        widths: [options.labelWidth, '*'],
        body: [
          [
            {
              text: label,
              bold: true,
              color: COLORS.teal,
              fontSize: 10,
              alignment: 'right',
              margin: [0, 0, 6, 0],
              fillColor: isGray ? COLORS.rowGray : '#ffffff'
            },
            {
              text: value || '',
              color: COLORS.grayText,
              fontSize: 10,
              margin: [6, 0, 8, 0],
              fillColor: isGray ? COLORS.rowGray : '#ffffff'
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 2,
        paddingBottom: () => 2
      },
      margin: [0, 0, 0, 0]
    };
  };
};

export interface PreviewPDFData {
  fechaDeArribo: string;
  establecimiento: string;
  emisor: string;
  emisorNombre?: string;
  emisorCuit?: string;
  emisorTipoLabel?: string;
  material: string;
  peso: string;
  unidadPeso?: string;
  comprobante: string;
  transporte: string;
  matricula: string;
  matriculaAcoplado: string;
  matriculaLocomotora?: string;
  matriculasVagones?: string;
  lineaFerrea?: string;
  transportista: string;
  cuitTransportista: string;
  itinerario: string;
  destinatario: string;
  cuitDestinatario: string;
  provincia: string;
  partido: string;
  localidad: string;
  direccion: string;
  transportistaRazonSocial: string;
  detalleUrl?: string;
}

export const generatePrevisualizacionPdf = async (data: PreviewPDFData): Promise<string> => {
  const labelColor = COLORS.teal;
  const headerBase64 = await imageUrlToDataUrl(guiaHeader);

  let infoRowIndex = 0;
  const infoRow = (label: string, value: string) => {
    const isGray = infoRowIndex % 2 === 0;
    infoRowIndex++;
    return {
      table: {
        widths: [100, '*'],
        body: [
          [
            {
              text: label,
              bold: true,
              color: labelColor,
              fontSize: 10,
              alignment: 'right',
              margin: [0, 0, 6, 0],
              fillColor: isGray ? '#e9e9e9' : '#ffffff'
            },
            {
              text: value || '',
              color: '#333',
              fontSize: 10,
              margin: [6, 0, 8, 0],
              fillColor: isGray ? '#e9e9e9' : '#ffffff'
            }
          ]
        ]
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 2,
        paddingBottom: () => 2
      },
      margin: [0, 0, 0, 0]
    };
  };

  const docDefinition = {
    pageSize: { width: 99 * mm, height: 260 * mm },
    pageOrientation: 'portrait',
    pageMargins: [20, 80, 20, 20],
    defaultStyle: { fontSize: 9 },
    header: buildHeaderImage(headerBase64, 99),
    watermark: {
      text: 'PREVISUALIZACIÓN',
      color: 'gray',
      opacity: 0.2,
      bold: true,
      italics: false
    },
    permissions: {
      modifying: false,
      copying: false,
      annotating: false,
      fillingForms: false,
      contentAccessibility: false,
      assembling: false
    },
    content: [
      {
        stack: buildTitleStack(
          { title: 16, law: 12, sigem: 12 },
          data.emisorTipoLabel || 'Productor / No productor',
          'Previsualización de guía'
        ),
        margin: [0, -6, 0, 10]
      },
      buildCenteredMeta('Guía N°', '000-00-000000-0000'),
      buildCenteredMeta('Fecha', dayjs().format('DD/MM/YYYY  HH:mm')),
      buildPreviewPlaceholder(),
      infoRow('Emisor:', data.emisorNombre || data.emisor || ''),
      infoRow('CUIT Emisor:', data.emisorCuit ? formatCuit(data.emisorCuit) : ''),
      data.establecimiento
        ? infoRow(
            data.transporte === 'Embarcación' ? 'Embarcación:' : 'Establecimiento:',
            data.establecimiento
          )
        : undefined,
      infoRow('Arribo estimado:', data.fechaDeArribo || ''),
      infoRow('Material:', data.material || ''),
      infoRow('Peso:', data.peso ? `${data.peso} ${data.unidadPeso || 'Tn'}` : ''),
      infoRow('Comprobante:', data.comprobante || ''),
      infoRow('Transporte:', data.transporte || ''),
      data.transporte === 'Tren'
        ? infoRow('Matrícula del tren:', data.matricula || '')
        : infoRow('Matrícula:', data.matricula || ''),
      data.transporte === 'Vehículo Automotor'
        ? infoRow('Mat. Acoplado:', data.matriculaAcoplado || '')
        : data.transporte === 'Tren'
          ? infoRow('Matrícula locomotora:', data.matriculaLocomotora || '')
          : undefined,
      data.transporte === 'Tren'
        ? infoRow('Matrícula vagones:', data.matriculasVagones || '')
        : undefined,
      data.transporte === 'Tren' ? infoRow('Línea férrea:', data.lineaFerrea || '') : undefined,
      data.transportistaRazonSocial && data.cuitTransportista
        ? infoRow('Transportista:', `${data.transportista}  |  CUIT: ${data.cuitTransportista}`)
        : undefined,
      data.itinerario ? infoRow('Itinerario principal', data.itinerario) : undefined,
      infoRow('Destinatario:', data.destinatario || ''),
      infoRow('CUIT:', data.cuitDestinatario || ''),
      infoRow('Provincia:', data.provincia || ''),
      infoRow('Partido:', data.partido || ''),
      infoRow('Localidad:', data.localidad || ''),
      infoRow('Destino:', data.direccion || '')
    ].filter(Boolean),
    styles: {
      bodyCell: { fontSize: 8 }
    }
  } as any;

  return new Promise((resolve) => {
    const pdfDocGenerator = pdfMake.createPdf(docDefinition as any);
    pdfDocGenerator.getBlob((blob: Blob) => {
      const url = URL.createObjectURL(blob);
      resolve(url);
    });
  });
};

export const generateGuiaPdf = async (data: Guia): Promise<string> => {
  const labelColor = COLORS.teal;
  const headerBase64 = await imageUrlToDataUrl(guiaHeader);

  const emisorTipoLabel = data.productor_minero
    ? 'Productor'
    : data.no_productor
      ? 'No Productor'
      : 'Productor / No productor';

  const emisorNombre =
    data.productor_minero?.titular?.razon_social || data.no_productor?.titular?.razon_social || '';
  const emisorCuit =
    data.productor_minero?.titular?.cuit ||
    data.no_productor?.titular?.cuit ||
    data.emisor_CUIT ||
    '';

  let infoRowIndex = 0;
  const infoRow = (label: string, value: string) => ({
    table: {
      widths: [100, '*'],
      body: [
        [
          {
            text: label,
            bold: true,
            color: labelColor,
            fontSize: 10,
            alignment: 'right',
            margin: [0, 0, 6, 0],
            fillColor: infoRowIndex % 2 === 0 ? '#e9e9e9' : '#ffffff'
          },
          {
            text: value || '',
            color: '#333',
            fontSize: 10,
            margin: [6, 0, 8, 0],
            fillColor: infoRowIndex++ % 2 === 0 ? '#e9e9e9' : '#ffffff'
          }
        ]
      ]
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 2,
      paddingBottom: () => 2
    },
    margin: [0, 0, 0, 0]
  });

  // Si hay URL de detalle, generar QR base64
  let qrBase64: string | undefined = undefined;

  const detalleUrl = `${API_URL}${ROUTES.ADMIN_GUIAS_DETAILS_ROUTE.replace(
    ':id',
    data.id?.toString() || ''
  )}`;

  if (detalleUrl) {
    const qr = new QRious({ value: detalleUrl, size: 800 });
    qrBase64 = qr.toDataURL();
  }

  // Contenido de una única copia (sin duplicado/triplicado)
  const content = [
    {
      stack: buildTitleStack({ title: 18, law: 16, sigem: 14 }, emisorTipoLabel),
      margin: [0, -6, 0, 10]
    },
    buildCenteredMeta('Guía N°', data.numero_completo || ''),
    buildCenteredMeta('Fecha', dayjs(data.fecha).format('DD/MM/YYYY  HH:mm')),
    buildQrBlock(qrBase64),
    infoRow('Emisor:', emisorNombre),
    infoRow('CUIT Emisor:', formatCuit(emisorCuit) || ''),
    (() => {
      if (data.medio_transporte === 'buque') {
        const buque = data.transporte?.buque || data.buque;
        const buqueLabel = buque
          ? [buque.nombre, buque.matricula_embarcacion].filter(Boolean).join(' - ')
          : '';
        return buqueLabel ? infoRow('Embarcación:', buqueLabel) : undefined;
      }
      return data.establecimiento?.nombre
        ? infoRow('Establecimiento:', data.establecimiento.nombre)
        : undefined;
    })(),
    infoRow(
      'Arribo estimado:',
      dayjs(data.destino_arribo_estimado).format('DD/MM/YYYY  HH:mm') || ''
    ),
    infoRow('Material:', data.material?.descripcion || ''),
    infoRow(
      'Peso:',
      data.cantidad
        ? `${data.cantidad} ${
            data.material?.unidad?.descripcion === 'METROS CUBICOS' ? 'm3' : 'Tn'
          }`
        : ''
    ),
    infoRow(
      'Comprobante:',
      `${data.tipo_documentacion_respaldatoria?.descripcion || ''} ${
        data.documentacion_numero_comprobante || ''
      }`.trim()
    ),
    infoRow(
      'Transporte:',
      data.medio_transporte === 'buque'
        ? 'Buque'
        : data.medio_transporte === 'tren'
          ? 'Tren'
          : 'Vehículo Automotor'
    ),
    data.medio_transporte === 'camion'
      ? infoRow('Matrícula:', data.transporte?.matricula || '')
      : data.medio_transporte === 'tren'
        ? infoRow('Matrícula del tren:', data.transporte?.matricula || '')
        : undefined,
    data.medio_transporte === 'camion' && data.transporte?.matricula_acoplado
      ? infoRow('Matrícula del acoplado:', data.transporte.matricula_acoplado || '')
      : undefined,
    data.medio_transporte === 'tren'
      ? infoRow('Matrícula locomotora:', data.transporte?.matricula_locomotora || '')
      : undefined,
    data.medio_transporte === 'tren'
      ? infoRow('Matrícula vagones:', data.transporte?.matriculas_vagones || '')
      : undefined,
    data.medio_transporte === 'tren'
      ? infoRow('Línea férrea:', data.transporte?.linea_ferrea || '')
      : undefined,
    data.transportista_razon_social && data.transportista_CUIT
      ? infoRow(
          'Transportista:',
          `${data.transportista_razon_social}  |  CUIT: ${formatCuit(data.transportista_CUIT)}`
        )
      : null,
    data.medio_transporte === 'camion'
      ? infoRow(
          'Itinerario principal',
          `${data.transporte.calle_principal ? data.transporte.calle_principal + ' | ' : ''}${(
            data.transporte.rutas_transitadas || []
          )
            .map((r) => r.ruta.descripcion)
            .join(', ')}`
        )
      : null,
    infoRow('Destinatario:', data.destino_nombre || ''),
    infoRow('CUIT:', data.destinatario_numero_identificacion || ''),
    infoRow('Provincia:', data.destino_provincia?.descripcion || ''),
    infoRow('Partido:', data.destino_partido?.descripcion || ''),
    infoRow('Localidad:', data.destino_localidad?.descripcion || ''),
    infoRow(
      'Destino:',
      `${data.destino_calle || ''} ${data.destino_numero || ''} ${data.destino_piso || ''} ${
        data.destino_departamento || ''
      }`.trim()
    )
  ].filter(Boolean);

  const docDefinition = {
    pageSize: { width: 99 * mm, height: 260 * mm },
    pageOrientation: 'portrait',
    pageMargins: [20, 90, 20, 20],
    defaultStyle: { fontSize: 9 },
    header: buildHeaderImage(headerBase64, 99),
    permissions: {
      modifying: false,
      copying: false,
      annotating: false,
      fillingForms: false,
      contentAccessibility: false,
      assembling: false
    },
    content: content.filter(Boolean),
    styles: {
      bodyCell: { fontSize: 8 }
    }
  } as any;

  return new Promise((resolve) => {
    const pdfDocGenerator = pdfMake.createPdf(docDefinition as any);
    pdfDocGenerator.getBlob((blob: Blob) => {
      const url = URL.createObjectURL(blob);
      resolve(url);
    });
  });
};
